import { confirmDialog } from "primereact/confirmdialog";
import { enqueueSnackbar } from "notistack";
import {
  createAstAssetNameCategory,
  createAstCategory,
  deleteAstAssetNameCategory,
  deleteAstCategory,
  getAllAstAssetNameCategories,
  getAstAssetCategories,
  getAstAssetNameCategoriesByCategory,
  updateAstAssetNameCategory,
  updateAstCategory,
} from "../../../services/ValitanApiService";

const parsePageResponse = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  const content = Array.isArray(payload?.content) ? payload.content : [];
  return {
    items: content,
    paginationInfo: {
      totalElements: payload.total_elements ?? payload.totalElements ?? content.length,
      totalPages: payload.total_pages ?? payload.totalPages ?? 1,
      pageNumber: payload.number ?? 0,
      pageSize: payload.size ?? content.length,
      sort: payload.sort ?? {},
    },
  };
};

const parseListResponse = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

export const getAssetCategories = async (page = 0, size = 100) => {
  try {
    const response = await getAstAssetCategories(page, size);
    if (response.status === 200) {
      return parsePageResponse(response);
    }
    enqueueSnackbar("Failed to fetch asset categories.", { variant: "error" });
    return { items: [], paginationInfo: {} };
  } catch (error) {
    console.error("Error fetching asset categories:", error);
    enqueueSnackbar("Error fetching asset categories. Please try again.", { variant: "error" });
    return { items: [], paginationInfo: {} };
  }
};

export const getSubCategoriesByCategory = async (assetCategoryId) => {
  try {
    const response = await getAstAssetNameCategoriesByCategory(assetCategoryId);
    if (response.status === 200) {
      return parseListResponse(response);
    }
    return [];
  } catch (error) {
    console.error("Error fetching sub-categories:", error);
    enqueueSnackbar("Error fetching sub-categories. Please try again.", { variant: "error" });
    return [];
  }
};

export const getAllSubCategoriesGrouped = async () => {
  try {
    const pageSize = 1000;
    let page = 0;
    let totalPages = 1;
    const allItems = [];

    while (page < totalPages) {
      const response = await getAllAstAssetNameCategories(page, pageSize);
      if (response.status !== 200) {
        break;
      }
      const { items, paginationInfo } = parsePageResponse(response);
      allItems.push(...items);
      totalPages = paginationInfo.totalPages || 1;
      page += 1;
    }

    const grouped = {};
    allItems.forEach((item) => {
      const parentId = item.asset_category_id ?? item.assetCategoryId;
      if (!parentId) return;
      if (!grouped[parentId]) {
        grouped[parentId] = [];
      }
      grouped[parentId].push(item);
    });

    return grouped;
  } catch (error) {
    console.error("Error fetching sub-categories:", error);
    enqueueSnackbar("Error loading sub-category counts. Please try again.", { variant: "error" });
    return {};
  }
};

export const validateCategoryForm = (name, code, setErrors) => {
  const errorsCopy = { name: "", code: "" };
  let valid = true;

  if (!name.trim()) {
    errorsCopy.name = "Category name is required";
    valid = false;
  }
  if (!code.trim()) {
    errorsCopy.code = "Code is required";
    valid = false;
  }

  setErrors(errorsCopy);
  return valid;
};

export const validateSubCategoryForm = (name, code, setErrors) => {
  const errorsCopy = { name: "", code: "" };
  let valid = true;

  if (!name.trim()) {
    errorsCopy.name = "Sub-category name is required";
    valid = false;
  }
  if (!code.trim()) {
    errorsCopy.code = "Code is required";
    valid = false;
  }

  setErrors(errorsCopy);
  return valid;
};

export const createOrUpdateCategory = async (
  event,
  { name, code, description },
  editingCategoryId,
  loadCategories,
  toggleModal,
  setErrors
) => {
  event.preventDefault();

  if (!validateCategoryForm(name, code, setErrors)) {
    return;
  }

  const payload = {
    name: name.trim(),
    code: code.trim().toUpperCase(),
    description: description?.trim() || null,
  };

  try {
    const response = editingCategoryId
      ? await updateAstCategory(editingCategoryId, payload)
      : await createAstCategory(payload);

    if (response.data?.status === "success") {
      enqueueSnackbar(response.data.message || "Category saved successfully.", { variant: "success" });
      toggleModal();
      await loadCategories();
    } else {
      enqueueSnackbar(response.data?.message || "Failed to save category.", { variant: "error" });
    }
  } catch (error) {
    console.error("Error saving category:", error);
    enqueueSnackbar(error.response?.data?.message || "Error saving category. Please try again.", {
      variant: "error",
    });
  }
};

export const createOrUpdateSubCategory = async (
  event,
  { name, code, assetCategoryId },
  editingSubCategoryId,
  onSaved,
  toggleModal,
  setErrors
) => {
  event.preventDefault();

  if (!validateSubCategoryForm(name, code, setErrors)) {
    return;
  }

  const payload = {
    name: name.trim(),
    code: code.trim().toUpperCase(),
    asset_category_id: assetCategoryId,
  };

  try {
    const response = editingSubCategoryId
      ? await updateAstAssetNameCategory(editingSubCategoryId, payload)
      : await createAstAssetNameCategory(payload);

    if (response.data?.status === "success") {
      enqueueSnackbar(response.data.message || "Sub-category saved successfully.", { variant: "success" });
      toggleModal();
      await onSaved();
    } else {
      enqueueSnackbar(response.data?.message || "Failed to save sub-category.", { variant: "error" });
    }
  } catch (error) {
    console.error("Error saving sub-category:", error);
    enqueueSnackbar(error.response?.data?.message || "Error saving sub-category. Please try again.", {
      variant: "error",
    });
  }
};

const deleteCategory = async (id, loadCategories) => {
  try {
    const response = await deleteAstCategory(id);
    if (response.data?.status === "success") {
      enqueueSnackbar(response.data.message || "Category deleted.", { variant: "success" });
      await loadCategories();
    } else {
      enqueueSnackbar(response.data?.message || "Failed to delete category.", { variant: "error" });
    }
  } catch (error) {
    enqueueSnackbar(error.response?.data?.message || "Failed to delete category.", { variant: "error" });
    console.error(error);
  }
};

export const confirmDeleteCategory = (id, loadCategories) => {
  confirmDialog({
    message: "Are you sure you want to delete this category? Related sub-categories may also be affected.",
    header: "Confirmation",
    icon: "pi pi-question-circle",
    accept: () => deleteCategory(id, loadCategories),
    reject: () => {},
  });
};

const deleteSubCategory = async (id, onDeleted) => {
  try {
    const response = await deleteAstAssetNameCategory(id);
    if (response.data?.status === "success") {
      enqueueSnackbar(response.data.message || "Sub-category deleted.", { variant: "success" });
      await onDeleted();
    } else {
      enqueueSnackbar(response.data?.message || "Failed to delete sub-category.", { variant: "error" });
    }
  } catch (error) {
    enqueueSnackbar(error.response?.data?.message || "Failed to delete sub-category.", { variant: "error" });
    console.error(error);
  }
};

export const confirmDeleteSubCategory = (id, onDeleted) => {
  confirmDialog({
    message: "Are you sure you want to delete this sub-category?",
    header: "Confirmation",
    icon: "pi pi-question-circle",
    accept: () => deleteSubCategory(id, onDeleted),
    reject: () => {},
  });
};
